import {
	Box,
	Flex,
	FormLabel,
	Grid,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputLeftElement,
	SimpleGrid,
	Switch,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { format, subDays } from 'date-fns';
import {
	MdTrendingUp,
	MdAttachMoney,
	MdOutlineShoppingCart,
	MdInsights,
	MdVpnKey,
	MdStorage,
	MdGroups,
	MdSearch
} from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { fetchDashboard } from '../../store/features/dashboard/dashboard.slice';
import { formatBRL } from '../../utils/format';

const SOURCE_COLORS = { DATABASE: '#0B4FD1', WESEND: '#39B8FF', NOVAVIDA: '#7C3AED' };
const SOURCE_LABELS = { DATABASE: 'Banco (cache)', WESEND: 'Wesend', NOVAVIDA: 'NovaVida (nova)' };

function MetricCard(props: { label: string; value: string; icon: any; color: string; hint?: string; highlight?: boolean }) {
	const bg = useColorModeValue('white', 'navy.800');
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	return (
		<Card bg={props.highlight ? 'cyan.500' : bg}>
			<Flex align='center' gap='12px'>
				<Flex
					align='center'
					justify='center'
					w='48px'
					h='48px'
					borderRadius='14px'
					bg={props.highlight ? 'whiteAlpha.300' : `${props.color}.50`}>
					<Icon as={props.icon} w='24px' h='24px' color={props.highlight ? 'white' : `${props.color}.500`} />
				</Flex>
				<Box>
					<Text fontSize='xs' fontWeight='700' color={props.highlight ? 'whiteAlpha.900' : subColor}>
						{props.label}
					</Text>
					<Text fontSize='22px' fontWeight='800' color={props.highlight ? 'white' : textColor}>
						{props.value}
					</Text>
					{props.hint && (
						<Text fontSize='10px' color={props.highlight ? 'whiteAlpha.800' : subColor}>
							{props.hint}
						</Text>
					)}
				</Box>
			</Flex>
		</Card>
	);
}

const STATUS_META: Record<string, { label: string; color: string }> = {
	SUCCESS: { label: 'Sucesso', color: 'green.500' },
	NOT_FOUND: { label: 'Não encontrado', color: 'orange.500' },
	ERROR: { label: 'Erro', color: 'red.500' }
};

export default function Dashboard() {
	const dispatch = useAppDispatch();
	const { overview, byClient, byIndicator, timeline, apiKeyUsage } = useAppSelector((state: RootState) => state.dashboard);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const tileBg = useColorModeValue('secondaryGray.300', 'navy.900');
	const gridColor = useColorModeValue('#E9EDF7', '#232D4A');
	const axisColor = useColorModeValue('#A3AED0', '#718096');

	const [from, setFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
	const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
	// Switch de visão: gráficos (default) x tabelas.
	const [tableView, setTableView] = useState(false);
	// Por padrão ADMIN/SUPORTE saem das contas; o switch os inclui.
	const [includeStaff, setIncludeStaff] = useState(false);
	const [clientSearch, setClientSearch] = useState('');

	useEffect(() => {
		dispatch(fetchDashboard({ from, to, includeStaff }));
	}, [dispatch, from, to, includeStaff]);

	const statusCounts = overview?.porStatus || [];
	const porFonte = overview?.porFonte || { DATABASE: 0, WESEND: 0, NOVAVIDA: 0 };

	const timelineLabels = timeline.map(p => format(new Date(`${p.date}T12:00:00`), 'dd/MM'));

	const baseChartOptions = {
		chart: { toolbar: { show: false }, fontFamily: 'inherit', foreColor: axisColor },
		grid: { borderColor: gridColor, strokeDashArray: 4 },
		dataLabels: { enabled: false },
		legend: { position: 'top' as const, horizontalAlign: 'left' as const },
		tooltip: { theme: 'light', shared: true, intersect: false }
	};

	// Leads por fonte (linha temporal, 3 séries).
	const sourceSeries = [
		{ name: SOURCE_LABELS.DATABASE, data: timeline.map(p => p.DATABASE) },
		{ name: SOURCE_LABELS.WESEND, data: timeline.map(p => p.WESEND) },
		{ name: SOURCE_LABELS.NOVAVIDA, data: timeline.map(p => p.NOVAVIDA) }
	];

	// Cobrado vs custo provedor (área temporal).
	const financeSeries = [
		{ name: 'Cobrado', data: timeline.map(p => Number(p.cobrado.toFixed(2))) },
		{ name: 'Custo provedor', data: timeline.map(p => Number(p.custo.toFixed(2))) }
	];

	const fonteTotal = porFonte.DATABASE + porFonte.WESEND + porFonte.NOVAVIDA;

	const filteredClients = useMemo(() => {
		const q = clientSearch.trim().toLowerCase();
		if (!q) return byClient;
		return byClient.filter(c => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
	}, [byClient, clientSearch]);

	return (
		<Box pt='10px'>
			<Flex
				justify='space-between'
				align={{ base: 'flex-start', md: 'center' }}
				direction={{ base: 'column', md: 'row' }}
				gap='12px'
				mb='20px'>
				<Text fontSize='xl' fontWeight='800' color={textColor}>
					{tableView ? 'Dados em tabela' : 'Visão geral'}
				</Text>
				<HStack spacing='16px' flexWrap='wrap'>
					<HStack>
						<Input type='date' variant='main' size='md' value={from} onChange={e => setFrom(e.target.value)} />
						<Text color={subColor}>até</Text>
						<Input type='date' variant='main' size='md' value={to} onChange={e => setTo(e.target.value)} />
					</HStack>
					<HStack>
						<FormLabel htmlFor='includeStaff' m='0' fontSize='sm' color={subColor}>
							Incluir ADMIN/SUPORTE
						</FormLabel>
						<Switch
							id='includeStaff'
							colorScheme='blue'
							isChecked={includeStaff}
							onChange={e => setIncludeStaff(e.target.checked)}
						/>
					</HStack>
					<HStack>
						<FormLabel htmlFor='tableView' m='0' fontSize='sm' color={subColor}>
							Ver em tabelas
						</FormLabel>
						<Switch id='tableView' colorScheme='blue' isChecked={tableView} onChange={e => setTableView(e.target.checked)} />
					</HStack>
				</HStack>
			</Flex>

			<SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing='20px' mb='20px'>
				<MetricCard label='VOLUME TOTAL' value={String(overview?.volumeTotal ?? 0)} icon={MdOutlineShoppingCart} color='brand' />
				<MetricCard label='TOTAL COBRADO' value={formatBRL(overview?.totalCobrado)} icon={MdAttachMoney} color='green' />
				<MetricCard
					label='CUSTO PROVEDOR'
					value={formatBRL(overview?.custoProvedor)}
					icon={MdTrendingUp}
					color='red'
					hint={`${porFonte.NOVAVIDA} novas × ${formatBRL(overview?.costPerQuery)}`}
				/>
				<MetricCard label='ROI' value={formatBRL(overview?.roi)} icon={MdInsights} color='cyan' highlight />
			</SimpleGrid>

			{tableView ? (
				<>
					{/* Tabela por usuário */}
					<Card mb='20px'>
						<Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap='10px' mb='12px'>
							<Flex align='center' gap='8px'>
								<Icon as={MdStorage} color='brand.500' />
								<Text fontWeight='700' color={textColor}>
									Por usuário
								</Text>
							</Flex>
							<InputGroup maxW='280px' size='sm'>
								<InputLeftElement pointerEvents='none'>
									<Icon as={MdSearch} color={subColor} />
								</InputLeftElement>
								<Input
									variant='main'
									placeholder='Buscar por nome ou e-mail'
									value={clientSearch}
									onChange={e => setClientSearch(e.target.value)}
								/>
							</InputGroup>
						</Flex>
						<Box overflowX='auto'>
							<Table variant='simple' size='sm'>
								<Thead>
									<Tr>
										<Th>Usuário</Th>
										<Th>Indicador</Th>
										<Th isNumeric>Leads</Th>
										<Th isNumeric>Cobrado</Th>
										<Th isNumeric>Custo</Th>
										<Th isNumeric>ROI</Th>
										<Th isNumeric>Saldo</Th>
									</Tr>
								</Thead>
								<Tbody>
									{filteredClients.map(c => (
										<Tr key={c.userId}>
											<Td>
												<Text fontWeight='600' color={textColor} fontSize='sm'>
													{c.name || 'Usuário removido'}
												</Text>
												<Text color={subColor} fontSize='xs'>
													{c.email || c.userId}
												</Text>
											</Td>
											<Td color={subColor}>{c.indicatorName || '—'}</Td>
											<Td isNumeric color={textColor}>{c.consultas}</Td>
											<Td isNumeric color='green.500' fontWeight='600'>{formatBRL(c.totalCobrado)}</Td>
											<Td isNumeric color={subColor}>{formatBRL(c.custoProvedor)}</Td>
											<Td isNumeric color='cyan.600' fontWeight='600'>{formatBRL(c.roi)}</Td>
											<Td isNumeric color={textColor}>{formatBRL(c.saldoAtual)}</Td>
										</Tr>
									))}
									{filteredClients.length === 0 && (
										<Tr>
											<Td colSpan={7}>
												<Text color={subColor} fontSize='sm'>
													Sem dados no período.
												</Text>
											</Td>
										</Tr>
									)}
								</Tbody>
							</Table>
						</Box>
					</Card>

					{/* Tabela por indicador */}
					<Card>
						<Flex align='center' gap='8px' mb='12px'>
							<Icon as={MdGroups} color='brand.500' />
							<Text fontWeight='700' color={textColor}>
								Por indicador
							</Text>
						</Flex>
						<Box overflowX='auto'>
							<Table variant='simple' size='sm'>
								<Thead>
									<Tr>
										<Th>Indicador</Th>
										<Th isNumeric>Clientes trazidos</Th>
										<Th isNumeric>Clientes ativos</Th>
										<Th isNumeric>Leads</Th>
										<Th isNumeric>Cobrado</Th>
										<Th isNumeric>ROI</Th>
									</Tr>
								</Thead>
								<Tbody>
									{byIndicator.map(i => (
										<Tr key={i.indicatorId || 'none'}>
											<Td fontWeight='600' color={textColor}>{i.name}</Td>
											<Td isNumeric color={textColor}>{i.clientesTrazidos}</Td>
											<Td isNumeric color={subColor}>{i.clientesAtivos}</Td>
											<Td isNumeric color={textColor}>{i.consultas}</Td>
											<Td isNumeric color='green.500' fontWeight='600'>{formatBRL(i.totalCobrado)}</Td>
											<Td isNumeric color='cyan.600' fontWeight='600'>{formatBRL(i.roi)}</Td>
										</Tr>
									))}
									{byIndicator.length === 0 && (
										<Tr>
											<Td colSpan={6}>
												<Text color={subColor} fontSize='sm'>
													Sem dados no período.
												</Text>
											</Td>
										</Tr>
									)}
								</Tbody>
							</Table>
						</Box>
					</Card>
				</>
			) : (
				<>
					{/* Leads por fonte (temporal) + donut de fontes */}
					<Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap='20px' mb='20px'>
						<Card>
							<Text fontWeight='700' color={textColor} mb='2px'>
								Leads por fonte ao longo do tempo
							</Text>
							<Text color={subColor} fontSize='sm' mb='10px'>
								Quantos leads vieram do banco, do Wesend e da NovaVida por dia.
							</Text>
							<ReactApexChart
								type='line'
								height={320}
								series={sourceSeries}
								options={{
									...baseChartOptions,
									colors: [SOURCE_COLORS.DATABASE, SOURCE_COLORS.WESEND, SOURCE_COLORS.NOVAVIDA],
									stroke: { curve: 'smooth', width: 3 },
									markers: { size: 4, hover: { size: 6 } },
									xaxis: { categories: timelineLabels },
									yaxis: { labels: { formatter: (v: number) => String(Math.round(v)) } }
								}}
							/>
						</Card>

						<Card>
							<Text fontWeight='700' color={textColor} mb='10px'>
								Distribuição por fonte
							</Text>
							{fonteTotal === 0 ? (
								<Text color={subColor} fontSize='sm'>
									Sem consultas no período.
								</Text>
							) : (
								<ReactApexChart
									type='donut'
									height={300}
									series={[porFonte.DATABASE, porFonte.WESEND, porFonte.NOVAVIDA]}
									options={{
										labels: [SOURCE_LABELS.DATABASE, SOURCE_LABELS.WESEND, SOURCE_LABELS.NOVAVIDA],
										colors: [SOURCE_COLORS.DATABASE, SOURCE_COLORS.WESEND, SOURCE_COLORS.NOVAVIDA],
										legend: { position: 'bottom' },
										dataLabels: { enabled: true },
										plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: 'Total' } } } } }
									}}
								/>
							)}
						</Card>
					</Grid>

					{/* Financeiro temporal + status */}
					<Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap='20px' mb='20px'>
						<Card>
							<Text fontWeight='700' color={textColor} mb='2px'>
								Cobrado vs custo provedor
							</Text>
							<Text color={subColor} fontSize='sm' mb='10px'>
								Receita (créditos cobrados) contra o custo da NovaVida por dia.
							</Text>
							<ReactApexChart
								type='area'
								height={300}
								series={financeSeries}
								options={{
									...baseChartOptions,
									colors: ['#01B574', '#EE5D50'],
									stroke: { curve: 'smooth', width: 2 },
									fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
									xaxis: { categories: timelineLabels },
									yaxis: { labels: { formatter: (v: number) => formatBRL(v) } }
								}}
							/>
						</Card>

						<Card>
							<Text fontWeight='700' color={textColor} mb='6px'>
								Consultas por status
							</Text>
							<Text color={subColor} fontSize='sm' mb='10px'>
								{overview?.consultasComCache ?? 0} de cache · {overview?.consultasNovas ?? 0} novas
							</Text>
							{statusCounts.map(s => {
								const meta = STATUS_META[s.status] || { label: s.status, color: 'secondaryGray.600' };
								const total = statusCounts.reduce((acc, x) => acc + x.count, 0) || 1;
								const pct = Math.round((s.count / total) * 100);
								return (
									<Box key={s.status} mb='14px'>
										<Flex justify='space-between' mb='4px'>
											<Text fontSize='sm' color={textColor} fontWeight='600'>
												{meta.label}
											</Text>
											<Text fontSize='sm' color={subColor}>
												{s.count} ({pct}%)
											</Text>
										</Flex>
										<Box bg={tileBg} borderRadius='8px' h='8px' overflow='hidden'>
											<Box bg={meta.color} h='100%' w={`${pct}%`} />
										</Box>
									</Box>
								);
							})}
						</Card>
					</Grid>

					{/* Uso por API key */}
					<Card mb='20px'>
						<Flex align='center' gap='8px' mb='2px'>
							<Icon as={MdVpnKey} color='brand.500' />
							<Text fontWeight='700' color={textColor}>
								Uso por API Key
							</Text>
						</Flex>
						<Text color={subColor} fontSize='sm' mb='10px'>
							Consultas feitas via API, por chave, no período.
						</Text>
						{apiKeyUsage.length === 0 ? (
							<Text color={subColor} fontSize='sm'>
								Nenhuma consulta via API no período.
							</Text>
						) : (
							<ReactApexChart
								type='bar'
								height={Math.max(160, apiKeyUsage.length * 48)}
								series={[{ name: 'Consultas', data: apiKeyUsage.map(k => k.consultas) }]}
								options={{
									...baseChartOptions,
									legend: { show: false },
									colors: ['#0B4FD1'],
									plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
									dataLabels: { enabled: true },
									xaxis: {
										categories: apiKeyUsage.map(k => (k.client ? `${k.name} · ${k.client}` : k.name))
									}
								}}
							/>
						)}
					</Card>

					{/* Top clientes */}
					<Card>
						<Flex align='center' gap='8px' mb='16px'>
							<Icon as={MdStorage} color='brand.500' />
							<Text fontWeight='700' color={textColor}>
								Top clientes
							</Text>
						</Flex>
						{byClient.length === 0 && (
							<Text color={subColor} fontSize='sm'>
								Sem dados no período.
							</Text>
						)}
						{byClient.slice(0, 10).map(c => (
							<Flex
								key={c.userId}
								justify='space-between'
								align='center'
								py='10px'
								borderBottom='1px solid'
								borderColor='secondaryGray.200'>
								<Box>
									<Text fontWeight='600' color={textColor} fontSize='sm'>
										{c.name || 'Usuário removido'}
									</Text>
									<Text color={subColor} fontSize='xs'>
										{c.indicatorName ? `${c.email || c.userId} · ${c.indicatorName}` : c.email || c.userId}
									</Text>
								</Box>
								<HStack spacing='24px'>
									<Box textAlign='right'>
										<Text fontSize='xs' color={subColor}>
											Volume
										</Text>
										<Text fontWeight='700' color={textColor}>
											{c.consultas}
										</Text>
									</Box>
									<Box textAlign='right'>
										<Text fontSize='xs' color={subColor}>
											Cobrado
										</Text>
										<Text fontWeight='700' color='green.500'>
											{formatBRL(c.totalCobrado)}
										</Text>
									</Box>
									<Box textAlign='right'>
										<Text fontSize='xs' color={subColor}>
											ROI
										</Text>
										<Text fontWeight='700' color='cyan.600'>
											{formatBRL(c.roi)}
										</Text>
									</Box>
								</HStack>
							</Flex>
						))}
					</Card>
				</>
			)}
		</Box>
	);
}
